import { firestore } from "firebase";

/**
 * A JSON report captured about a physical device by the production test rig, uploaded through the
 * Cloud provisioning API (`POST /provisioning/v1/deviceReport`), stored in Cloud Storage and
 * indexed by a document in the device's `deviceReports` subcollection.
 *
 * Office is a **reader only**: the provisioning API is the sole writer and goes through the Admin
 * SDK, so the security rules grant no client write at all.
 *
 * Contract: IRES Cloud `functions/docs/apis/provisioning-api.md`.
 */

/** Cloud Storage root everything device-report lives under. */
export const DEVICE_REPORTS_ROOT = "deviceReports";

/** Firestore subcollection under a device document that indexes its reports. */
export const DEVICE_REPORTS_COLLECTION = "deviceReports";

/** Holding area for reports that could not yet be attached to a device document. */
export const UNASSIGNED_PREFIX = `${DEVICE_REPORTS_ROOT}/unassigned`;

/** Extension of a report object, whose name is the report id plus this. */
export const DEVICE_REPORT_FILE_EXTENSION = ".json";

/**
 * The kind of report.
 *
 * There is deliberately **no `INITIAL_TEST`**: whether a test report is the *initial* one is a
 * function of ordering (the earliest stored report for a device), not a property of the report, so
 * it is derived at render time and never stored. More kinds are expected, which is why the UI must
 * not assume there is only one.
 */
export enum DeviceReportType {
  TEST_REPORT = "TEST_REPORT",
}

/**
 * The device class a report belongs to, as the **string** the Cloud API persists.
 *
 * Mirrors the Cloud-side enum. Office's own `IresDeviceCategory` is a numeric enum and knows only
 * PANEL and COMBOX, so it can neither name these values nor represent a sensor.
 */
export enum DeviceReportDeviceCategory {
  COM_BOX = "COM_BOX",
  SENSOR = "SENSOR",
  PANEL = "PANEL",
}

/** How a report reached its final location. */
export enum DeviceReportOrigin {
  /** Stored straight under an existing device document. */
  DIRECT = "DIRECT",
  /** Waited in `unassigned/bySerialNumber` until the device document was created. */
  CLAIMED_BY_SERIAL = "CLAIMED_BY_SERIAL",
  /** Waited in `unassigned/byMacAddress` because the upload carried no serial number. */
  CLAIMED_BY_MAC_ADDRESS = "CLAIMED_BY_MAC_ADDRESS",
}

/** Which identifier an unassigned report is waiting under. */
export enum UnassignedReportKey {
  SERIAL_NUMBER = "bySerialNumber",
  MAC_ADDRESS = "byMacAddress",
}

/** One document of `{comboxes|sensors|panels}/{serialNo}/deviceReports/{reportId}`. */
export interface DeviceReport {
  /** The document id, which is also the report's Storage folder name. */
  id: string;
  reportType: DeviceReportType;
  serialNo: string;
  deviceCategory: string;
  macAddress: string;
  /** Canonical 12 upper-case hex digits. The only value to match a MAC on. */
  macAddressHex: string;
  reportedAt: firestore.Timestamp;
  uploadedAt: firestore.Timestamp;
  /** Object name **with a leading slash**, matching `Report.storageLocation`. */
  storageLocation: string;
  fileSizeInBytes: number;
  /** `pass` / `fail`, lifted from the payload by the API. Absent when it carried none. */
  overallResult?: string | null;
  origin: DeviceReportOrigin;
  claimedAt?: firestore.Timestamp;
  previousStorageLocation?: string;
  /** Label of the provisioning caller that uploaded it, kept through a claim. */
  createdBy: string;
}

/**
 * A report still waiting in `deviceReports/unassigned/`.
 *
 * These have **no Firestore document** — that is the invariant the whole feature rests on: a report
 * is indexed if and only if it sits under a device document that exists. So everything here comes
 * from Cloud Storage: the object name, and the upload envelope the API writes as custom metadata.
 */
export interface UnassignedDeviceReport {
  /** Report folder name, e.g. `20260810T104442Z_TEST_REPORT`. */
  id: string;
  /** Which identifier it is filed under. */
  key: UnassignedReportKey;
  /** The serial or the canonical MAC it is filed under. */
  keyValue: string;
  /** Object name of the JSON, without a leading slash. */
  objectName: string;
  reportType: string;
  /** From the upload envelope; `null` when the object carries no usable metadata. */
  reportedAt: Date | null;
  macAddress: string | null;
  serialNo: string | null;
  overallResult: string | null;
  createdBy: string | null;
  sizeInBytes: number;
}

/**
 * Firestore collection holding a device, from its serial.
 *
 * Deliberately **not** `getIresDeviceCollectionName` from `IresDevice.ts`: Office's
 * `IresDeviceCategory` knows only PANEL and COMBOX, so it resolves a sensor serial (`WLB00010`) to
 * `panels` and a report would be looked for under a device that does not exist. Device reports
 * exist for all three classes, so this mirrors the Cloud-side resolver instead, which is the one
 * that decided where the report was written.
 */
export function getDeviceReportCollectionName(serialNo: string): string {
  const upperCase = serialNo.toUpperCase();

  if (upperCase.startsWith("C") || upperCase.startsWith("B")) {
    return "comboxes";
  }

  return upperCase.startsWith("W") ? "sensors" : "panels";
}

/**
 * Report id split back into its parts: `20260810T104442Z_TEST_REPORT`.
 *
 * The id is the report's identity (its object name without `.json`, and its Firestore document id),
 * so it is the authority on when the report was captured and what kind it is. Unassigned reports
 * have no document, so this is how their list is built when an object's custom metadata is missing.
 *
 * @returns The decoded values, or `null` if the id was not produced by the provisioning API.
 */
export function parseReportId(
  reportId: string
): { reportedAt: Date; reportType: string } | null {
  const match = /^(\d{4})(\d{2})(\d{2})T(\d{2})(\d{2})(\d{2})Z_(.+)$/.exec(reportId);

  if (!match) {
    return null;
  }

  const [, year, month, day, hour, minute, second, reportType] = match;
  const reportedAt = new Date(
    `${year}-${month}-${day}T${hour}:${minute}:${second}Z`
  );

  // `new Date` rolls an out-of-range but well-formed value over (month 99 becomes a later year),
  // so the round trip is checked rather than assumed.
  if (Number.isNaN(reportedAt.getTime()) || reportedAt.toISOString().slice(0, 4) !== year) {
    return null;
  }

  return { reportedAt, reportType };
}

/**
 * Strips the leading slash a stored `storageLocation` carries, which a Storage reference must not
 * have. Exactly one character, never a blanket `replace`, so a path is never otherwise altered.
 */
export function toStorageObjectName(storageLocation: string): string {
  return storageLocation.startsWith("/") ? storageLocation.substring(1) : storageLocation;
}
